import { ApiError, type Api } from "../api/client.js";
import { confirmSheet, openSheet, type SheetHandle } from "../ui/sheet.js";
import { SecurityView } from "../ui/security.js";
import type { AimHandlers } from "../ui/aim.js";
import type { Tab } from "../ui/tabs.js";
import { LOCKED_STATUS, UNLOCK_TAKES, securityModel, type SecurityModel } from "./security-model.js";

/**
 * 19 Security's controller (DCTA-111 run 2): the bank's reads (`GET /v1/accounts` for the lock,
 * `GET /v1/webauthn/credentials` for the passkeys, `GET /v1/cards` for whether there is a card to
 * freeze) and the one action the lab serves here — Lock my money, `POST /v1/protection/actions`
 * `lock_funds`: one sheet, no passkey (§E.2). DCTA-231 V2 (lab lockfix 20083ab): the lock ends every
 * OTHER session of this customer (the lost phone) and keeps this one — so the locked screen is the
 * bank's read after the lock (its answer only if that read cannot be made), the customer still sees
 * balances and freezes cards, and the app is told the bank changed (`onLocked`: Home and Invest read
 * it too). Only a real sign-out (the bank answers 401) lands on the door. DCTA-232 F1: locked, Unlock
 * my money is handed to the app (`onUnlock`, `UNLOCK_WORDS`); this screen asks the bank nothing for it.
 */
export interface SecurityDeps {
  readonly api: Api;
  /** The column the sheets rise in (`#app`). */
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  readonly onBack: () => void;
  /** Freeze a card → 15 Cards (absent until there is a cards screen to open). */
  readonly onFreeze?: (() => void) | undefined;
  readonly onSignedOut: () => void;
  /** DCTA-231 V2: the money is locked now — the screens that show it read again (Home, Invest). */
  readonly onLocked?: () => void;
  /** DCTA-232 F1: Unlock my money — the app opens the conversation with `UNLOCK_WORDS` (main.ts). */
  readonly onUnlock: () => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
}

export class SecurityScreen {
  readonly view: SecurityView;
  readonly #d: SecurityDeps;
  #model: SecurityModel | undefined;
  #freeze = false;
  #sheet: SheetHandle | undefined;
  #locking = false;
  /** The lock did not go on and its loud line is up: Try again locks (the customer already confirmed). */
  #failed = false;
  /** DCTA-232 F1: Unlock my money was tapped on this showing (one request; `refresh()` opens it again). */
  #unlocking = false;

  constructor(deps: SecurityDeps) {
    this.#d = deps;
    this.view = new SecurityView({
      onBack: deps.onBack,
      onLock: () => this.#confirm(),
      onUnlock: () => this.#unlock(),
      onRetry: () => (this.#failed ? void this.#lock(undefined) : void this.refresh()),
      onFreeze: () => deps.onFreeze?.(),
      onTab: deps.onTab,
      assistant: deps.assistant,
    });
  }

  async refresh(): Promise<void> {
    this.#unlocking = false;
    if (this.#model === undefined) this.view.loading();
    const read = await this.#read();
    if (read === "signed-out") {
      this.#d.onSignedOut();
      return;
    }
    if (read === "failed") {
      this.#model = undefined;
      this.view.unreachable();
    }
  }

  /** The bank's three reads, drawn — or why they could not be: a real sign-out, or a bank that did not answer. */
  async #read(): Promise<"drawn" | "signed-out" | "failed"> {
    try {
      const [accounts, credentials, cards] = await Promise.all([
        this.#d.api.accounts(),
        this.#d.api.credentials(),
        this.#d.api.cards().catch(() => ({ cards: [] })),
      ]);
      this.#model = securityModel({ accounts: accounts.accounts, credentials: credentials.credentials });
      // A card the fast path can freeze today: one linked to an account (backend ask: a card-level target).
      this.#freeze = this.#d.onFreeze !== undefined && cards.cards.some((c) => c.account_id !== undefined && !c.frozen);
      this.view.show(this.#model, { freeze: this.#freeze });
      return "drawn";
    } catch (error) {
      return error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED" ? "signed-out" : "failed";
    }
  }

  /** Lock my money: the one sheet (§E.6 copy), the act and Not now. */
  #confirm(): void {
    if (this.#sheet !== undefined || this.#locking) return;
    const close = (): void => void this.#close();
    const { sheet, primary } = confirmSheet(
      {
        icon: "i-lock",
        title: "Lock your money?",
        lead: "Payments, transfers and investments stop while it's locked. You can still see balances and freeze cards.",
        fine: UNLOCK_TAKES,
        act: "Lock my money",
        keep: "Not now",
      },
      { onAct: () => void this.#lock(primary), onKeep: close },
    );
    this.#sheet = openSheet(this.#d.host, sheet, { behind: this.view.parts, reduced: this.#d.reduced(), onDismiss: close });
  }

  #unlock(): void {
    // MUTATION TARGET: without the guard a double tap sends the words twice — two turns, two requests (e2e zz-locks).
    if (this.#unlocking) return;
    this.#unlocking = true;
    this.#d.onUnlock();
  }

  async #close(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }

  /** The bank is asked once; the sheet holds (locked, the act busy) until it answers. */
  async #lock(primary: HTMLButtonElement | undefined): Promise<void> {
    if (this.#locking) return;
    this.#locking = true;
    primary?.setAttribute("aria-busy", "true");
    this.#sheet?.lock(true);
    try {
      const r = await this.#d.api.lockFunds();
      this.#sheet?.lock(false);
      await this.#close();
      this.#failed = false;
      this.view.notLocked(false);
      // DCTA-231 V2: the session stays — the locked state is the bank's read now; its answer only if the read cannot be made.
      // MUTATION TARGET: without the read the screen is drawn from the app's own guess, and Home and Invest are never told.
      const read = await this.#read();
      if (read === "signed-out") {
        this.#d.onSignedOut();
        return;
      }
      if (read === "failed") {
        const was = this.#model;
        this.#model = { locked: true, status: LOCKED_STATUS, passkeys: was?.passkeys ?? [] };
        this.view.show(this.#model, { freeze: this.#freeze });
      }
      this.view.done(r.result_summary);
      this.#d.onLocked?.();
    } catch (error) {
      this.#sheet?.lock(false);
      await this.#close();
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      // §E.2: a protective action that did not go on says so, loudly.
      this.#failed = true;
      this.view.notLocked(true);
    } finally {
      this.#locking = false;
      primary?.setAttribute("aria-busy", "false");
    }
  }
}
